import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BinIcon from '../../../../components/Icons/BinIcon';
import Edit02Icon from '../../../../components/Icons/edit-02';
import PlusIcon from '../../../../components/Icons/PlusIcon';
import { colors, themePalette } from '../../../../components/ThemeProvider/themeColors';
import { EmployeeRow } from './model/types';

interface EmployeesTableProps {
  employees: EmployeeRow[];
  hasIncompleteEmployee?: boolean;
  duplicateIndices?: Set<number>;
  onAdd?: () => void;
  onEdit?: (idx: number, employee: EmployeeRow) => void;
  onDelete?: (index: number) => void;
  onDeleteAll?: () => void;
  showValidationErrors?: boolean;
}

const EmployeesTable = ({
  employees,
  hasIncompleteEmployee = false,
  duplicateIndices = new Set(),
  onAdd,
  onEdit,
  onDelete,
  onDeleteAll,
  showValidationErrors,
}: EmployeesTableProps) => {
  const { t } = useTranslation('salary-card');
  const isIncomplete = (e: EmployeeRow) =>
    !e.customerName || !e.fin || !e.serial || !e.gsm || !e.employeePosition;
  const columns = [
    { label: t('employeesTable.columns.fullName'), flex: 2 },
    { label: t('employeesTable.columns.pin'), flex: 1 },
    { label: t('employeesTable.columns.serial'), flex: 1 },
    { label: t('employeesTable.columns.phone'), flex: 1.5 },
    { label: t('employeesTable.columns.position'), flex: 1 },
  ];

  const hasDuplicates = duplicateIndices.size > 0;
  const hasRowError = (idx: number) =>
    Boolean(hasIncompleteEmployee && isIncomplete(employees[idx])) || duplicateIndices.has(idx);

  const sortedEmployees = employees
    .map((employee, originalIdx) => ({ employee, originalIdx }))
    .sort((a, b) => {
      if (!showValidationErrors) return 0;

      return Number(hasRowError(b.originalIdx)) - Number(hasRowError(a.originalIdx));
    });

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        boxShadow: '0px 0px 32px 0px rgba(0, 0, 0, 0.06)',
        borderRadius: '12px',
        mt: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '36px 28px',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography fontSize={18} fontWeight={600} lineHeight='28px'>
          {t('employeesTable.title')}
        </Typography>
        <Button color='secondary' onClick={onAdd}>
          <PlusIcon sx={{ width: '18px', height: '18px' }} />
          {t('employeesTable.addNew')}
        </Button>
      </Box>
      {employees.length > 0 && (
        <Box sx={{ padding: '0 28px', overflowX: 'auto' }}>
          {showValidationErrors && hasIncompleteEmployee && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: colors.error[50],
                border: `1px solid ${colors.error[200]}`,
              }}
            >
              <Typography fontSize={13} fontWeight={500} color={colors.error[700]}>
                {t('employeesTable.errors.incomplete')}
              </Typography>
            </Box>
          )}
          {showValidationErrors && hasDuplicates && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: '8px',
                backgroundColor: colors.error[50],
                border: `1px solid ${colors.error[200]}`,
              }}
            >
              <Typography fontSize={13} fontWeight={500} color={colors.error[700]}>
                {t('employeesTable.errors.duplicates')}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              padding: '0px 0px 12px 0px ',
              minWidth: '860px',
            }}
          >
            {columns.map(col => (
              <Typography
                key={col.label}
                sx={{ flex: col.flex }}
                fontSize={13}
                fontWeight={500}
                color={themePalette.text_and_icon.third_party}
              >
                {col.label}
              </Typography>
            ))}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                minWidth: '100px',
                justifyContent: 'flex-end',
              }}
              onClick={onDeleteAll}
            >
              <Typography
                fontSize={13}
                fontWeight={500}
                color={themePalette.text_and_icon.third_party}
              >
                {t('employeesTable.deleteAll')}
              </Typography>
              <BinIcon />
            </Box>
          </Box>

          {sortedEmployees.map(({ employee, originalIdx }, displayIdx) => {
            const hasError = showValidationErrors && hasRowError(originalIdx);

            return (
              <Box
                key={originalIdx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minWidth: '860px',
                  borderBottom:
                    displayIdx < sortedEmployees.length - 1
                      ? `1px solid ${colors.base[100]}`
                      : 'none',
                  padding: '16px 0',
                  backgroundColor: hasError ? colors.error[50] : 'transparent',
                }}
              >
                <Typography sx={{ flex: 2 }} fontSize={14} fontWeight={600} noWrap>
                  {employee.customerName}
                </Typography>
                <Typography sx={{ flex: 1 }} fontSize={14} fontWeight={600}>
                  {employee.fin}
                </Typography>
                <Typography sx={{ flex: 1 }} fontSize={14} fontWeight={600}>
                  {employee.serial}
                </Typography>
                <Typography sx={{ flex: 1.5 }} fontSize={14} fontWeight={600}>
                  {employee.gsm}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  fontSize={14}
                  fontWeight={600}
                >
                  {employee.employeePosition}
                </Typography>
                <Box
                  sx={{ display: 'flex', gap: 1.5, minWidth: '100px', justifyContent: 'flex-end' }}
                >
                  <IconButton
                    sx={{ padding: '10px', backgroundColor: colors.base[50], borderRadius: '8px' }}
                    onClick={() => onDelete?.(originalIdx)}
                  >
                    <BinIcon />
                  </IconButton>

                  <IconButton
                    sx={{ padding: '10px', backgroundColor: colors.base[50], borderRadius: '8px' }}
                    onClick={() => onEdit?.(originalIdx, employee)}
                  >
                    <Edit02Icon sx={{ width: '16px', height: '16px' }} />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default EmployeesTable;
